import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { Star, X, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, type Forum, type BoursoUser } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import { FollowCard } from "@/components/cards/FollowCard";
import { useAuth } from "@/hooks/useAuth";
import { usePlan, FREE_LIMITS } from "@/hooks/usePlan";
import { openUpgrade } from "@/components/UpgradeDialog";
import { LoginRequired } from "@/components/LoginRequired";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { usePathTab } from "@/lib/usePathTab";
import { fromNowFR } from "@/lib/utils";

const FOLLOW_TABS = ["all", "forum", "user", "thread"] as const;
type FollowTab = (typeof FOLLOW_TABS)[number];

// Free-tier usage meter : "X / 3 suivis" + upgrade CTA. Renders nothing for Pro.
function FreeUsage({ count }: { count: number }) {
  const { isFree, isLoading } = usePlan();
  if (isLoading || !isFree) return null;
  const max = FREE_LIMITS.maxFollows;
  const atLimit = count >= max;
  return (
    <Card className={atLimit ? "border-primary/40" : undefined}>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm text-foreground">
            <span className="font-semibold">{Math.min(count, max)} / {max}</span> suivis utilisés
            <span className="ml-2 text-xs text-foreground-muted">(plan gratuit)</span>
          </p>
          <p className="text-xs text-foreground-muted">
            {atLimit
              ? "Vous avez atteint la limite. Passez à Pro pour un suivi illimité."
              : "Passez à Pro pour suivre autant de forums, fils et utilisateurs que vous voulez."}
          </p>
        </div>
        <Button size="sm" variant={atLimit ? "default" : "outline"} onClick={() => openUpgrade()}>
          <Crown className="size-4" />
          Passer à Pro
        </Button>
      </CardContent>
    </Card>
  );
}

type Follow = {
  id: string | number;
  target_type: "forum" | "user" | "thread";
  target_id: string | number;
  created_at: string;
};

export default function FollowsPage() {
  useSeo({ title: "Mes suivis", canonicalPath: "/follows", noindex: true });
  const qc = useQueryClient();
  const [tab, setTab] = usePathTab<FollowTab>("tab", "/follows", "all", FOLLOW_TABS);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // /api/follows answers 401 → empty list, so the rest of the page would
  // render an "empty state". We short-circuit and show a friendly explainer
  // instead. Hooks above this point keep the rules-of-hooks happy.
  const showLoginGate = !authLoading && !isAuthenticated;

  const { data = [], isLoading } = useQuery({
    queryKey: ["follows"],
    queryFn: () => api.follows() as Promise<Follow[]>,
    retry: false,
    enabled: !showLoginGate,
  });

  // Pull the forums + users lists already cached so we can show real names.
  const { data: forums = [] } = useQuery({
    queryKey: ["forums"],
    queryFn: () => api.forums(),
    staleTime: 60_000,
  });
  const { data: users = [] } = useQuery({
    queryKey: ["users", ""],
    queryFn: () => api.users(""),
    staleTime: 60_000,
  });

  const forumById = useMemo(() => {
    const m = new Map<number, Forum>();
    for (const f of forums) m.set(f.id, f);
    return m;
  }, [forums]);
  const userById = useMemo(() => {
    const m = new Map<number, BoursoUser>();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: data.length, forum: 0, user: 0, thread: 0 };
    for (const f of data) c[f.target_type]++;
    return c;
  }, [data]);

  const filtered = useMemo(() => {
    if (tab === "all") return data;
    return data.filter((f) => f.target_type === tab);
  }, [data, tab]);

  const del = useMutation({
    mutationFn: (id: number) => api.deleteFollow(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["follows"] });
      toast.success("Suivi retiré");
    },
  });

  function renderTarget(f: Follow): React.ReactNode {
    const tid = Number(f.target_id);
    if (f.target_type === "forum") {
      const forum = forumById.get(tid);
      return (
        <Link to={`/forums/${tid}/threads`} className="hover:underline">
          {forum?.title ?? forum?.slug ?? `Forum #${tid}`}
        </Link>
      );
    }
    if (f.target_type === "user") {
      const u = userById.get(tid);
      return (
        <Link to={`/users/${tid}`} className="hover:underline">
          {u?.username ?? `User #${tid}`}
        </Link>
      );
    }
    if (f.target_type === "thread") {
      return (
        <Link to={`/threads/${tid}`} className="hover:underline">
          Thread #{tid}
        </Link>
      );
    }
    return `#${tid}`;
  }

  if (showLoginGate) {
    return (
      <div className="flex flex-col gap-6">
        <header>
          <h1 className="text-2xl font-semibold text-foreground">Mes suivis</h1>
        </header>
        <LoginRequired
          icon={Star}
          title="Connectez-vous pour gérer vos suivis"
          description={
            <>
              En suivant des forums, des sujets ou des utilisateurs, vous
              recevez des alertes en temps réel à chaque nouveauté.
              Connectez-vous (par code email) pour démarrer.
            </>
          }
          bullets={[
            "Suivez un forum pour recevoir les nouveaux sujets et les sujets relancés.",
            "Suivez un utilisateur pour être prévenu à chaque post.",
            "Suivez un sujet pour ne plus en manquer une réponse.",
          ]}
        />
      </div>
    );
  }

  const FOLLOW_TAB_LABELS: Record<FollowTab, string> = {
    all: "Tous",
    forum: "Forums",
    user: "Utilisateurs",
    thread: "Sujets",
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-baseline gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold shrink-0 text-foreground">
          Mes suivis
        </h1>
        <p className="hidden sm:block text-sm text-foreground-muted truncate">
          {data.length} suivi{data.length > 1 ? "s" : ""} ·{" "}
          {counts.forum} forum{counts.forum > 1 ? "s" : ""},{" "}
          {counts.user} utilisateur{counts.user > 1 ? "s" : ""},{" "}
          {counts.thread} sujet{counts.thread > 1 ? "s" : ""}
        </p>
      </header>

      <FreeUsage count={data.length} />

      <Card>
        <CardContent className="p-4">
          {/* Native select on mobile, pills on sm+ */}
          <NativeSelect
            className="sm:hidden"
            label="Filtre"
            value={tab}
            onValueChange={(v) => setTab(v as FollowTab)}
            options={FOLLOW_TABS.map((t) => ({
              value: t,
              label: FOLLOW_TAB_LABELS[t],
              count: counts[t] ?? 0,
            }))}
          />
          <div className="hidden sm:flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-muted">Filtre</span>
            <div className="flex flex-wrap gap-2">
              {FOLLOW_TABS.map((t) => (
                <Button
                  key={t}
                  variant={tab === t ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTab(t)}
                >
                  {FOLLOW_TAB_LABELS[t]}
                  <span className="ml-1 text-xs opacity-70">({counts[t] ?? 0})</span>
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-foreground-muted text-sm">
            {tab === "all"
              ? "Vous ne suivez rien pour l'instant. Cliquez sur « Suivre » depuis un forum ou un utilisateur."
              : "Aucun suivi dans cette catégorie."}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile : card list */}
          <div className="md:hidden flex flex-col gap-2">
            {filtered.map((f) => (
              <FollowCard
                key={String(f.id)}
                type={f.target_type}
                createdAt={f.created_at}
                onRemove={() => del.mutate(Number(f.id))}
              >
                {renderTarget(f)}
              </FollowCard>
            ))}
          </div>

          {/* Desktop : table */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead className="min-w-[12rem]">Cible</TableHead>
                  <TableHead>Depuis</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((f) => (
                  <TableRow key={String(f.id)}>
                    <TableCell>
                      <Badge variant="secondary">{f.target_type}</Badge>
                    </TableCell>
                    <TableCell className="min-w-[12rem]">{renderTarget(f)}</TableCell>
                    <TableCell className="text-xs text-foreground-muted">
                      {fromNowFR(f.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => del.mutate(Number(f.id))}
                        title="Retirer"
                        aria-label="Retirer ce suivi"
                      >
                        <X className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
